// src/components/profiles/Member/components/testimonies/video/VideoTestimonies.tsx
"use client";

import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useRouter } from "next/navigation";
import { AppDispatch } from "../../../../../../redux/storeTypes";
import {
  Plus, Upload, CloudUpload, Check, X, Trash2, Image as ImageIcon, Clapperboard, Play, MoveRight
} from "lucide-react";

import {
  validateVideoFile,
  validateImageFile,
} from "../../../../../../utils/validators/ugc/mediaValidator";

import {
  TESTIMONY_VIDEO_POLICY,
  TESTIMONY_IMAGE_POLICY,
} from "../../../../../../utils/validators/ugc/ugcPolicies";

import {
  fetchSummary,
  fetchDetailBySlug,
  createTestimony,
  updateTestimonyBySlug,
  deleteTestimonyBySlug,
  resetOpStatus,
} from "../../../../../../redux/ugc/testimoniesSlice";
import {
  selectSummary,
  selectSummaryStatus,
  selectDetailBySlug,
  selectDetailStatusBySlug,
  selectOpStatus,
} from "../../../../../../redux/selectors/ugc/testimoniesSelectors";
import { makeMediaUrl } from "../../../../../../utils/navigation/contentNavigation";

import { normalizeUploadFile } from "../../../../../../utils/mediaTools/mimeUtils";
import type { TestimonyDTO } from "../../../../../../types/ugc/testimonyTypes";
import { useAlertMessage } from "../../../../../../hooks/common/useAlertMessage";
import {
  ActionMenu,
  Modal,
  ProgressCircle,
  VideoCardSkeleton,
  Alert
} from "../../../../../../components/common";
import TestimonyThumbCropper from "../../../../../../components/common/avatar/testimony-avatar/TestimonyThumbCropper";
import { UGC_ENDPOINTS } from "../../../../../../services/ugc/endpoint/endpoints";

import MediaConversionPanel from "../../../../../../components/mediaConversion/MediaConversionPanel/MediaConversionPanel";
import { useMediaConversionJobs } from "../../../../../../hooks/mediaConversion/useMediaConversionJobs";
import PrivateCdnImage from "@/asset-delivery/components/PrivateCdnImage";
import type { PrivateCdnTarget } from "@/asset-delivery/components/PrivateCdnImage";
import { toCleanCdnUrl } from "@/asset-delivery/utils/toCleanCdnUrl";


import "./VideoTestimonies.css";


// Stable identity for cookie warmup (CDN-only)
const buildPrivateCdnTargetFromTestimony = (it: TestimonyDTO | undefined): PrivateCdnTarget | null => {
  const oid = (it as any)?.id;
  if (typeof oid !== "number") return null;

  return {
    app_label: "posts",
    model: "testimony",
    object_id: oid,
  };
};



// Prepare video + thumb once: normalize names/types and validate via UGC policies -----------------
async function prepareVideoAndThumb(video: File | null, thumb: File | null) {
  if (!video) return { error: "Please choose a video file." };
  if (!thumb) return { error: "Please choose a thumbnail image." };

  // 1) Normalize both (fallback mime/type if missing)
  const normVideo = normalizeUploadFile(video, "video");
  const normThumb = normalizeUploadFile(thumb, "image");

  // 2) Validate thumb (sync)
  const imgRes = validateImageFile(normThumb, TESTIMONY_IMAGE_POLICY);
  if (!imgRes.ok) {
    return { error: imgRes.error || "Invalid thumbnail image." };
  }

  // 3) Validate video (async: reads metadata for duration)
  const vidRes = await validateVideoFile(normVideo, TESTIMONY_VIDEO_POLICY);
  if (!vidRes.ok) {
    return { error: vidRes.error || "Invalid video file." };
  }

  return { video: normVideo, thumb: normThumb };
}


// Extract error message from API response ----------------------------------------------------------
function extractApiError(e: any, fallback = "Upload failed.") {
  const data = e?.response?.data;

  if (!data) return e?.message || fallback;
  if (typeof data === "string") return data;

  // DRF common
  if (typeof data?.detail === "string") return data.detail;

  // Field errors: { video: ["..."], thumbnail: ["..."] }
  const pickFirst = (v: any) => (Array.isArray(v) ? v[0] : typeof v === "string" ? v : null);

  const candidates = [
    pickFirst(data.video),
    pickFirst(data.thumbnail),
    pickFirst(data.image),
    pickFirst(data.non_field_errors),
  ].filter(Boolean) as string[];

  return candidates[0] || e?.message || fallback;
}



// props ------------------------------------------------------------------------------------------
interface Props {
  isActive?: boolean;
}

// component --------------------------------------------------------------------------------------
const VideoTestimonies: React.FC<Props> = ({ isActive = true }) => {
  const dispatch = useDispatch<AppDispatch>();
  const router = useRouter();
  const { message, showMessage, clearMessage } = useAlertMessage();

  /* ---------- Global state ---------- */
  const summary = useSelector(selectSummary);
  const summaryStatus = useSelector(selectSummaryStatus);

  const videoSummary = summary?.video ?? { exists: false };
  const videoSlug = videoSummary.exists ? videoSummary.slug : undefined;

  const videoDetail = useSelector(
    videoSlug ? selectDetailBySlug(videoSlug) : () => undefined
  ) as TestimonyDTO | undefined;

  const detailStatus = useSelector(
    videoSlug ? selectDetailStatusBySlug(videoSlug) : () => "idle"
  ); // "idle" | "loading" | "succeeded" | "failed"

  /* ---------- Local UI ---------- */
  const [showCreateForm, setShowCreateForm] = useState(false);

  /* video + thumbnail (create) */
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [thumbFile, setThumbFile] = useState<File | null>(null);

  const createVideoInputRef = useRef<HTMLInputElement | null>(null);
  const createThumbInputRef = useRef<HTMLInputElement | null>(null);

  /* replace inputs */
  const replaceVideoInputRef = useRef<HTMLInputElement | null>(null);
  const replaceThumbInputRef = useRef<HTMLInputElement | null>(null);

  /* ---------- Derived flags ---------- */
  const baseLoading = summaryStatus === "loading";
  const detailLoading = !!videoSlug && detailStatus === "loading";
  const isLoading = baseLoading || detailLoading;

  const isEmpty = !isLoading && videoSummary.exists === false;

  /* ---------- Op status ---------- */
  const createKey = "create:video";
  const updatingKey = `update:video:${videoSlug ?? "x"}`;
  const deletingKey = `delete:video:${videoSlug ?? "x"}`;

  const updateStatus = useSelector(selectOpStatus(updatingKey));
  const deleteStatus = useSelector(selectOpStatus(deletingKey));

  /* ---------- Upload progress & abort ---------- */
  const [uploadPct, setUploadPct] = useState<number | null>(null);

  type UploadKind = "create" | "replace-video" | "replace-thumb" | null;
  const [uploadKind, setUploadKind] = useState<UploadKind>(null);

  const abortRef = useRef<AbortController | null>(null);
  const isUploading = uploadPct !== null;

  // cropping state
  const [thumbToCrop, setThumbToCrop] = useState<string | null>(null);
  const [showThumbCrop, setShowThumbCrop] = useState(false);
  const [cropMode, setCropMode] = useState<"create" | "replace" | null>(null);

  const [validatingVideo, setValidatingVideo] = useState(false);

  const canCreate = !!videoFile && !!thumbFile && !isUploading && !validatingVideo;

  // when uploading, show progress; when not, we may show converting/loading skeleton
  const showUploadOverlayCreate =
    isUploading && uploadKind === "create";

  const showUploadOverlay =
    isUploading && ["replace-video", "replace-thumb"].includes(uploadKind ?? "");

  const mediaTarget = videoSlug
    ? {
      by: "slug" as const,
      appLabel: "posts",
      model: "testimony",
      slug: videoSlug,
    }
    : null;


  const {
    jobs: conversionJobs,
  } = useMediaConversionJobs(mediaTarget, {
    enabled: Boolean(mediaTarget) && isActive,
    fieldName: "video",
  });

  const hasConversionJobs = conversionJobs.length > 0;

  const hasActiveConversion = conversionJobs.some(
    (j) => j.status === "queued" || j.status === "processing"
  );


  // Convert dataUrl to File -----------------------------------------------------------------------------
  async function dataUrlToFile(dataUrl: string, filename = `thumb_${Date.now()}.jpg`): Promise<File> {
    const res = await fetch(dataUrl);
    const blob = await res.blob();
    return new File([blob], filename, { type: "image/jpeg" });
  }


  // CDN-only poster (thumbnail) ---------------------------------------------------------------
  // We do NOT use resolvePlayback (no signed URLs). We only convert whatever backend gives us
  // (s3 presigned / key / old urls) into a clean CDN url.
  const poster = React.useMemo(() => {
    const raw =
      (videoDetail as any)?.thumbnail ??             // preferred
      (videoDetail as any)?.thumbnail_signed_url ??  // legacy (still ok: will be converted)
      null;

    return toCleanCdnUrl(raw);
  }, [(videoDetail as any)?.thumbnail, (videoDetail as any)?.thumbnail_signed_url]);

  const privatePosterTarget = React.useMemo(
    () => buildPrivateCdnTargetFromTestimony(videoDetail),
    [videoDetail?.id]
  );


  // Abort controller to cancel upload -----------------------------------------------------------------------
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
      abortRef.current = null;
    };
  }, []);

  const startNewAbortController = () => {
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    return ctrl;
  };

  const handleAbortUpload = () => {
    abortRef.current?.abort();
    abortRef.current = null;
    setUploadPct(null);
    setUploadKind(null);
    showMessage("Upload canceled.", "warning");
  };

  /* ---------- Effects ---------- */
  useEffect(() => {
    if (summaryStatus === "idle" || summaryStatus === "failed") {
      dispatch(fetchSummary());
    }
  }, [dispatch, summaryStatus]);

  useEffect(() => {
    if (!videoSlug) return;

    if (!hasConversionJobs) return;

    if (hasActiveConversion) return;

    if (!videoDetail && detailStatus !== "loading") {
      dispatch(fetchDetailBySlug(videoSlug));
    }
  }, [
    dispatch,
    videoSlug,
    videoDetail,
    detailStatus,
    hasConversionJobs,
    hasActiveConversion,
  ]);



  /* ---------- Handlers: Create ---------- */
  const doCreate = async (video: File, thumb: File | null) => {
    const prep = await prepareVideoAndThumb(video, thumb);
    if (prep.error) return showMessage(prep.error, "error");

    const fd = new FormData();
    fd.append("type", "video");
    fd.append("video", prep.video!, prep.video!.name || "upload.mp4");
    fd.append("thumbnail", prep.thumb!, prep.thumb!.name || "poster.jpg");

    const ctrl = startNewAbortController();
    setUploadKind("create");
    setUploadPct(0);

    try {
      const res = await dispatch(
        createTestimony({
          type: "video",
          payload: fd,
          onUploadProgress: setUploadPct,
          signal: ctrl.signal,
        })
      ).unwrap();

      setShowCreateForm(false);
      setVideoFile(null);
      setThumbFile(null);

      if (createVideoInputRef.current) createVideoInputRef.current.value = "";
      if (createThumbInputRef.current) createThumbInputRef.current.value = "";

      // showMessage("Video testimony created.", "success");
      dispatch(resetOpStatus(createKey));
    } catch (e: any) {
      const status = e?.response?.status;
      const existingSlug = e?.response?.data?.existing_slug;
      if (status === 409 && existingSlug) {
        const ok = window.confirm("You already have a video testimony. Replace the existing one?");
        // if (!ok) return showMessage("Replace canceled.", "info");

        const fd2 = new FormData();
        fd2.append("video", prep.video!, prep.video!.name);
        fd2.append("thumbnail", prep.thumb!, prep.thumb!.name);

        const ctrl2 = startNewAbortController();
        setUploadKind("replace-video");
        setUploadPct(0);

        try {
          const rep = await dispatch(
            updateTestimonyBySlug({
              type: "video",
              slug: existingSlug,
              payload: fd2,
              onUploadProgress: setUploadPct,
              signal: ctrl2.signal,
            })
          ).unwrap();

          setShowCreateForm(false);
          setVideoFile(null);
          setThumbFile(null);
          // showMessage("Video testimony replaced.", "success");
          dispatch(resetOpStatus(updatingKey.replace(":x", `:${existingSlug}`)));
        } catch (uErr: any) {
          showMessage(uErr?.message || "Failed to replace video testimony.", "error");
        }
      } else {
        showMessage(extractApiError(e, "Failed to create video testimony."), "error");
      }
    } finally {
      setUploadPct(null);
      setUploadKind(null);
      abortRef.current = null;
    }
  };

  // -----------------------------------------------------
  const handleCreateUpload = async () => {
    if (!videoFile) return showMessage("Please choose a video file.", "error");
    if (!thumbFile) return showMessage("Please choose a thumbnail image.", "error");
    await doCreate(videoFile, thumbFile);
  };


  /* ---------- Handlers: Replace video ---------- */
  const handleReplaceVideo = async (file: File | null) => {
    if (!videoSlug) return;
    if (replaceVideoInputRef.current) replaceVideoInputRef.current.value = "";

    if (!file) return showMessage("Please choose a video file.", "error");

    // 1) Normalize filename/mime (no transcode)
    const normVideo = normalizeUploadFile(file, "video");

    // 2) Validate via policy (duration + dynamic size cap)
    const vidRes = await validateVideoFile(normVideo, TESTIMONY_VIDEO_POLICY);
    if (!vidRes.ok) {
      return showMessage(vidRes.error || "Invalid video file.", "error");
    }


    // 3) Build form data
    const fd = new FormData();
    fd.append("video", normVideo, normVideo.name);

    // 4) Upload
    const ctrl = startNewAbortController();
    setUploadKind("replace-video");
    setUploadPct(0);

    try {
      const r = await dispatch(
        updateTestimonyBySlug({
          type: "video",
          slug: videoSlug,
          payload: fd,
          onUploadProgress: setUploadPct,
          signal: ctrl.signal,
        })
      ).unwrap();

      // showMessage("Video replaced. Converting…", "success");
      dispatch(resetOpStatus(updatingKey));
    } catch (e: any) {
      if (e?.name === "CanceledError" || e?.code === "ERR_CANCELED") {
        showMessage("Upload canceled.", "warning");
      } else {
        showMessage(extractApiError(e, "Failed to replace video."), "error");
      }
    } finally {
      setUploadPct(null);
      setUploadKind(null);
      abortRef.current = null;
    }
  };


  /* ---------- Handlers: Replace thumb ---------- */
  const handleReplaceThumb = async (file: File | null) => {
    if (!videoSlug) return;
    if (replaceThumbInputRef.current) replaceThumbInputRef.current.value = "";

    if (!file) return showMessage("Please choose a thumbnail image.", "error");

    const normThumb = normalizeUploadFile(file, "image");

    // Validate thumb via policy
    const imgRes = validateImageFile(normThumb, TESTIMONY_IMAGE_POLICY);
    if (!imgRes.ok) {
      return showMessage(imgRes.error || "Invalid thumbnail image.", "error");
    }

    // 3) Build form data
    const fd = new FormData();
    fd.append("thumbnail", normThumb, normThumb.name);

    // 4) Upload
    const ctrl = startNewAbortController();
    setUploadKind("replace-thumb");
    setUploadPct(0);

    try {
      const r = await dispatch(
        updateTestimonyBySlug({
          type: "video",
          slug: videoSlug,
          payload: fd,
          onUploadProgress: setUploadPct,
          signal: ctrl.signal,
        })
      ).unwrap();

      // showMessage("Thumbnail updated.", "success");
      dispatch(resetOpStatus(updatingKey));
    } catch (e: any) {
      if (e?.name === "CanceledError" || e?.code === "ERR_CANCELED") {
        showMessage("Upload canceled.", "warning");
      } else {
        showMessage(e?.message || "Failed to update thumbnail.", "error");
      }
    } finally {
      setUploadPct(null);
      setUploadKind(null);
      abortRef.current = null;
    }
  };

  const handlePickCreateVideo = async (file: File | null) => {
    if (!file) return;

    setValidatingVideo(true);

    try {
      const norm = normalizeUploadFile(file, "video");
      const res = await validateVideoFile(norm, TESTIMONY_VIDEO_POLICY);

      if (!res.ok) {
        setVideoFile(null);
        if (createVideoInputRef.current) createVideoInputRef.current.value = "";
        showMessage(res.error || "This video doesn't meet upload rules.", "error");
        return;
      }

      setVideoFile(file);
      // showMessage("Video selected.", "info");
    } catch (err: any) {
      setVideoFile(null);
      if (createVideoInputRef.current) createVideoInputRef.current.value = "";
      showMessage(err?.message || "Could not read video metadata.", "error");
    } finally {
      setValidatingVideo(false);
    }
  };


  /* ---------- Delete ---------- */
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteAck, setDeleteAck] = useState(false);

  const openDeleteModal = () => {
    setDeleteAck(false);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!videoSlug) return;
    try {
      await dispatch(deleteTestimonyBySlug({ type: "video", slug: videoSlug })).unwrap();
      // showMessage("Video testimony deleted.", "success");
      setShowDeleteModal(false);
      dispatch(fetchSummary());
      dispatch(resetOpStatus(deletingKey));
    } catch (e: any) {
      showMessage(extractApiError(e, "Failed to delete."), "error");
    }
  };

  /* ---------- Renders ---------- */
  const renderCreateForm = () => (
    <div className="t-video-card">
      <div className="t-video-card-body">
        {/* Video picker */}
        <input
          ref={createVideoInputRef}
          type="file"
          accept="video/mp4,video/webm,video/ogg,video/quicktime,video/x-matroska,video/x-msvideo,video/3gpp,video/3gpp2"
          className="hidden-file-input"
          onChange={(e) => {
            const f = e.target.files?.[0] ?? null;
            handlePickCreateVideo(f);
          }}
        />

        {/* Thumb picker */}
        <input
          ref={createThumbInputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          className="hidden-file-input"
          onChange={(e) => {
            const f = e.target.files?.[0] ?? null;
            if (!f) return;
            // open cropper with selected image
            const reader = new FileReader();
            reader.onloadend = () => {
              setThumbToCrop(reader.result as string);
              setCropMode("create");
              setShowThumbCrop(true);
              if (createThumbInputRef.current) createThumbInputRef.current.value = "";
            };
            reader.readAsDataURL(f);
          }}
        />


        <div className="picker-row">
          <button
            type="button"
            className="vt-icon-upload-btn"
            onClick={() => !isUploading && createVideoInputRef.current?.click()}
            aria-label="Upload video file"
            disabled={isUploading}
          >
            <CloudUpload size={28} />
            <span>Video</span>
          </button>
          <Plus className="vt-icon-plus-btn" size={24} strokeWidth={2} />
          <button
            type="button"
            className="vt-icon-upload-btn ghost"
            onClick={() => !isUploading && createThumbInputRef.current?.click()}
            aria-label="Upload thumbnail"
            disabled={isUploading}
          >
            <ImageIcon size={28} />
            <span>Thumbnail</span>
          </button>
        </div>

        {/* Filenames */}
        <div className="file-list">
          {videoFile && <div className="file-chip"><Clapperboard size={16} /> {videoFile.name}</div>}
          {thumbFile && <div className="file-chip"><ImageIcon size={16} /> {thumbFile.name}</div>}
        </div>
      </div>

      {/* Alerts */}
      {message && (
        <div className="vt-alert-box">

          <div className="vt-alert-host" onClick={(e) => e.stopPropagation()}>
            <Alert
              type={message.type as any}
              closable
              onClose={clearMessage}
              autoCloseDuration={7000}
            >
              {message.text}
            </Alert>
          </div>
        </div>
      )}

      {/* Upload progress overlay (create) */}
      {showUploadOverlayCreate && (
        <div className="progress-overlay">
          <ProgressCircle
            value={uploadPct ?? 0}
            onCancel={handleAbortUpload}
            cancelable
            label="Uploading"
            layout="overlay"
            size={72}
            stroke={6}
            rounded
            glow
            backdrop
            backdropOpacity={0.7}
            backdropBlur={3}
          />
        </div>
      )}

      {/* Actions */}
      {(videoFile || thumbFile) && (
        <div className="t-video-card-actions">
          <button
            type="button"
            className="x-icon-action"
            onClick={() => {
              setShowCreateForm(false);
              setVideoFile(null);
              setThumbFile(null);
              if (createVideoInputRef.current) createVideoInputRef.current.value = "";
              if (createThumbInputRef.current) createThumbInputRef.current.value = "";
            }}
            aria-label="Cancel"
          >
            <X size={32} />
          </button>

          <button
            type="button"
            className={`check-icon-action ${canCreate ? "" : "is-disabled"}`}
            onClick={canCreate && !isUploading ? handleCreateUpload : undefined}
            disabled={!canCreate || isUploading}
            aria-label={canCreate ? "Create video testimony" : "Select both video and thumbnail first"}
            title={canCreate ? "Create video testimony" : "Select both video and thumbnail first"}
          >
            <Check size={32} />
          </button>
        </div>
      )}

    </div>
  );

  const renderEmpty = () => (
    <div className="video-empty" onClick={() => setShowCreateForm(true)}>
      <div className="video-empty-action">
        <Plus className="plus-btn" size={32} strokeWidth={3} />
        <MoveRight className="move-icone" size={32} />
        <Clapperboard className="video-icon" size={36} strokeWidth={1} />
      </div>
      <p className="empty-hint">
        Share your video testimony and let your story inspire others in TownLIT.
      </p>
    </div>
  );


  // Render conversion state (no video detail yet)
  const renderConverting = () => {
    if (!mediaTarget) return null;

    return (
      <div className="t-video-card t-video-card--processing">
        <div className="t-video-card-body">

          {/* Processing placeholder */}
          <div className="poster-clickable is-processing">
            <MediaConversionPanel
              target={mediaTarget}
              fieldName="video"
              compact
              title="Processing video"
              density="mini"
            />
          </div>

        </div>
      </div>
    );
  };


  const renderReady = () => {
    if (!videoDetail) return null;

    // always send endpoint relative to baseApi.baseURL (no /v1 here)
    const TESTIMONY_ENDPOINT = UGC_ENDPOINTS.testimonies.root;

    const watchUrl = videoDetail?.slug
      ? makeMediaUrl(videoDetail.slug, {
        endpoint: TESTIMONY_ENDPOINT,
        scope: "flat", // flat content viewer
        title: videoDetail.title || undefined,
        aspect: "auto",
        usePoster: false,
      })
      : null;

    const replaceDisabled = updateStatus === "loading" || isUploading;
    const deleteDisabled = deleteStatus === "loading" || isUploading;

    const actionOptions = [
      {
        label: replaceDisabled ? "Updating…" : "Replace video",
        icon: <Upload size={16} />,
        disabled: replaceDisabled,
        onClick: () => replaceVideoInputRef.current?.click(),
      },
      {
        label: replaceDisabled ? "Updating…" : "Replace thumbnail",
        icon: <ImageIcon size={16} />,
        disabled: replaceDisabled,
        onClick: () => replaceThumbInputRef.current?.click(),
      },
      {
        label: deleteDisabled ? "Deleting…" : "Delete",
        icon: <Trash2 size={16} />,
        disabled: deleteDisabled,
        danger: true,
        onClick: openDeleteModal,
      },
    ];

    return (
      <div className="t-video-card t-video-card--minimal">
        <div className="t-video-card-body">

          <div
            className="poster-clickable"
            role="button"
            aria-label="Play video"
            onClick={() => watchUrl && router.push(watchUrl)}
          >
            {/* 0) Poster (CDN-only) - MUST be the first layer */}
            {poster && privatePosterTarget ? (
              <PrivateCdnImage
                src={poster}    
                placeholderSrc={null}    
                alt={videoDetail.title || "Video testimony poster"}
                className="t-video-poster"
                imgClassName="t-video-poster__img"
                target={privatePosterTarget}
                kind={"thumbnail" as any}
                fieldName={"thumbnail" as any}
                rootMargin="1200px"
                threshold={0.01}
                failOpen
                imgLoading="eager"
                fetchPriority="high"
              />
            ) : null}

            {/* 1) Top-right menu */}
            <div
              className="video-card-actions-top-right"
              onClick={(e) => e.stopPropagation()}
            >
              <ActionMenu
                options={actionOptions}
                position="bottom-right"
                iconSize={32}
                color="secondary"
              />
            </div>

            {/* 2) Play button — centered */}
            <button
              type="button"
              className="uvv-play"
              aria-label="Play"
              onClick={(e) => {
                e.stopPropagation();
                watchUrl && router.push(watchUrl);
              }}
            >
              <Play size={34} strokeWidth={1} />
            </button>

            {/* 3) Upload progress overlay */}
            {showUploadOverlay && (
              <div className="progress-overlay">
                <ProgressCircle
                  value={uploadPct ?? 0}
                  onCancel={handleAbortUpload}
                  cancelable
                  label="Uploading"
                  layout="overlay"
                  size={64}
                  stroke={2}
                  backdrop
                  backdropOpacity={0.7}
                  backdropBlur={3}
                />
              </div>
            )}
          </div>

          {/* hidden inputs for replace (not visible) */}
          <input
            ref={replaceVideoInputRef}
            type="file"
            accept="video/mp4,video/webm,video/ogg,video/quicktime,video/x-matroska,video/x-msvideo,video/3gpp,video/3gpp2"
            className="hidden-file-input"
            onChange={(e) => {
              const f = e.target.files?.[0] ?? null;
              handleReplaceVideo(f || null);
            }}
          />
          <input
            ref={replaceThumbInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="hidden-file-input"
            onChange={(e) => {
              const f = e.target.files?.[0] ?? null;
              if (!f) return;
              const reader = new FileReader();
              reader.onloadend = () => {
                setThumbToCrop(reader.result as string);
                setCropMode("replace");
                setShowThumbCrop(true);
                if (replaceThumbInputRef.current) replaceThumbInputRef.current.value = "";
              };
              reader.readAsDataURL(f);
            }}
          />

        </div>
      </div>
    );
  };



  return (
    <div className="t-video-wrap">

      {/* Initial skeleton */}
      {isLoading && (
        <VideoCardSkeleton
          variant="ultrawide"
          standalone
          showTitle
          className="t-video-skel"
        />
      )}

      {!isLoading && (
        <>
          {/* No video yet */}
          {isEmpty && !showCreateForm && renderEmpty()}
          {isEmpty && showCreateForm && renderCreateForm()}

          {/* Video exists but converting */}
          {!isEmpty && hasActiveConversion && renderConverting()}

          {/* Video ready */}
          {!isEmpty && !hasActiveConversion && renderReady()}
        </>
      )}

      {/* Delete Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => {
          if (deleteStatus !== "loading") setShowDeleteModal(false);
        }}
        title="Delete video testimony?"
        message={
          <div>
            <p>
              This will permanently delete your video testimony from TownLIT.
              You can upload a new one later, but this file cannot be recovered.
            </p>
            <label className="confirm-row" style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 12 }}>
              <input type="checkbox" checked={deleteAck} onChange={(e) => setDeleteAck(e.target.checked)} />
              <span>I understand this action cannot be undone.</span>
            </label>
          </div>
        }
        confirmText={deleteStatus === "loading" ? "Deleting…" : "Delete"}
        cancelText="Cancel"
        onConfirm={handleDeleteConfirm}
        disableBackdropClick={deleteStatus === "loading"}
        isLoading={deleteStatus === "loading"}
        confirmVariant="danger"
        confirmDisabled={!deleteAck}
      />

      {/* thumb cropper modal (468x200) */}
      {showThumbCrop && thumbToCrop && (
        <TestimonyThumbCropper
          imageSrc={thumbToCrop}
          onDone={async (dataUrl) => {
            const file = await dataUrlToFile(dataUrl, `thumb_468x200_${Date.now()}.jpg`);
            if (cropMode === "create") setThumbFile(file);
            else if (cropMode === "replace") await handleReplaceThumb(file);
            setShowThumbCrop(false); setCropMode(null); setThumbToCrop(null);
          }}
          onCancel={() => {
            setShowThumbCrop(false);
            setCropMode(null);
            setThumbToCrop(null);
          }}
        />
      )}
    </div>
  );
};

export default VideoTestimonies;

